import React, {useCallback, useMemo} from "react";
import {createEditor, Editor, Element as SlateElement, Range, Transforms} from "slate";
import {Editable, Slate, withReact} from "slate-react";
import isUrl from "is-url";

const BlogEditor = ({ content, setContent }) => {
    const editor = useMemo(() => withReact(createEditor()), []);

    const initialValue = useMemo(() => {
        return Array.isArray(content)
            ? content
            : [{ type: "paragraph", children: [{ text: String(content) }] }];
    }, [content]);

    // --- MARKS (inline styles) ---
    const renderLeaf = useCallback(({ attributes, children, leaf }) => {
        if (leaf.italic) children = <em>{children}</em>;
        if (leaf.underline) children = <u>{children}</u>;
        return <span {...attributes}>{children}</span>;
    }, []);

    // --- BLOCKS (headings, lists) ---
    const renderElement = useCallback(({ attributes, children, element }) => {
        switch (element.type) {
            case "heading-one":
                return <h1 {...attributes} className="text-2xl font-bold mb-2">{children}</h1>;
            case "heading-two":
                return <h2 {...attributes} className="text-xl font-semibold mb-2">{children}</h2>;
            case "bulleted-list":
                return <ul {...attributes} className="list-disc pl-6 mb-2">{children}</ul>;
            case "list-item":
                return <li {...attributes}>{children}</li>;
            case "link":
                return (
                    <a
                        {...attributes}
                        href={element.url}
                        className="text-blue-600 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {children}
                    </a>
                );
            default:
                return <p {...attributes} className="mb-2">{children}</p>;
        }
    }, []);

    // --- MARK TOGGLES ---
    const toggleMark = (format) => {
        const isActive = isMarkActive(format);
        isActive
            ? Editor.removeMark(editor, format)
            : Editor.addMark(editor, format, true);
    };

    const isMarkActive = (format) => {
        const marks = Editor.marks(editor);
        return marks ? marks[format] === true : false;
    };

    // --- BLOCK TOGGLES ---
    const toggleBlock = (format) => {
        const isActive = isBlockActive(editor, format);
        const isList = format === "bulleted-list";


        Transforms.unwrapNodes(editor, {
            match: n =>
                !Editor.isEditor(n) &&
                SlateElement.isElement(n) &&
                ["bulleted-list"].includes(n.type),
            split: true,
        });

        let newType = isActive ? "paragraph" : isList ? "list-item" : format;
        Transforms.setNodes(editor, { type: newType });

        if (!isActive && isList) {
            const block = { type: format, children: [] };
            Transforms.wrapNodes(editor, block);
        }
    };

    const isBlockActive = (editor, format) => {
        const [match] = Editor.nodes(editor, {
            match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
        });
        return !!match;
    };

    const insertLink = () => {
        const url = window.prompt("Enter the URL:");
        if (!url || !isUrl(url)) return;

        const { selection } = editor;
        const isCollapsed = selection && Range.isCollapsed(selection);

        const link = {
            type: "link",
            url,
            children: isCollapsed ? [{ text: url }] : [],
        };

        if (isCollapsed) {
            Transforms.insertNodes(editor, link);
        } else {
            Transforms.wrapNodes(editor, link, { split: true });
            Transforms.collapse(editor, { edge: "end" });
        }
    };

    const handleChange = (value) => {
        setContent(value);
    };

    return (
        <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Blog Content</label>

            {/* Toolbar */}
            <div className="flex flex-wrap gap-2 mb-2">
                <button onClick={() => Editor.undo(editor)} className="px-2 py-1 border rounded text-sm hover:bg-orange-100">Undo</button>
                <button onClick={() => Editor.redo(editor)} className="px-2 py-1 border rounded text-sm hover:bg-orange-100">Redo</button>

                {/* Marks */}
                <button onClick={() => toggleMark("italic")} className="px-2 py-1 border rounded text-sm hover:bg-orange-100">Italic</button>
                <button onClick={() => toggleMark("underline")} className="px-2 py-1 border rounded text-sm hover:bg-orange-100">Underline</button>

                {/* Blocks */}
                <button onClick={() => toggleBlock("heading-one")} className="px-2 py-1 border rounded text-sm hover:bg-orange-100">H1</button>
                <button onClick={() => toggleBlock("heading-two")} className="px-2 py-1 border rounded text-sm hover:bg-orange-100">H2</button>
                <button onClick={() => toggleBlock("bulleted-list")} className="px-2 py-1 border rounded text-sm hover:bg-orange-100">Bullets</button>

                <button onClick={insertLink} className="px-2 py-1 border rounded text-sm hover:bg-orange-100">Add Link</button>
            </div>

            {/* Editor */}
            <div className="border border-gray-200 rounded-lg p-2 focus-within:ring-2 focus-within:ring-orange-600">
                <Slate editor={editor} initialValue={initialValue} onChange={handleChange}>
                    <Editable
                        className="min-h-[200px] p-2 text-sm text-gray-900 outline-none"
                        renderElement={renderElement}
                        renderLeaf={renderLeaf}
                    />
                </Slate>
            </div>
        </div>
    );
};

export default BlogEditor;
